// src/utils/gamificationSeason.js
// Client mirror of the season-window math in functions/gamificationEngine.js
// (seasonWindowFor / addDays). Must stay in lockstep: the seasonId this
// computes is the doc id the UI reads (seasons/{id}, missionProgress/{id}_*).
// All-day strings 'YYYY-MM-DD', UTC arithmetic, [startDay, endDay) half-open.

const DAY_MS = 24 * 60 * 60 * 1000;

// Same validation as the backend normalizer's coerceIsoDay
// (functions/gamificationConfig.js): shape check PLUS a Date round-trip.
// The round-trip matters — Date.parse('2026-02-31T00:00:00Z') does NOT fail,
// it rolls over to March 3, so a regex-only check would let the UI accept and
// render a season the engine rejects (Codex review 2026-07-05: admin saves an
// impossible date, hosts see a live-looking season, engine never runs).
export function isIsoDay(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

function isoDayToUtcMs(day) {
  return Date.parse(`${day}T00:00:00Z`);
}

export function addDays(day, days) {
  return new Date(isoDayToUtcMs(day) + days * DAY_MS).toISOString().slice(0, 10);
}

export function todayIsoDay(baseDate = new Date()) {
  // Melbourne-local calendar day (the engine's day basis).
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(baseDate); // en-CA => YYYY-MM-DD
}

export function seasonWindowFor(missions, todayDay = todayIsoDay()) {
  if (!missions || missions.enabled !== true) return null;
  if (!isIsoDay(missions.seasonStart) || !isIsoDay(todayDay)) return null;
  const lengthWeeks = Number(missions.seasonLengthWeeks);
  if (!Number.isInteger(lengthWeeks) || lengthWeeks <= 0) return null;
  if (todayDay < missions.seasonStart) return null;

  const lengthDays = lengthWeeks * 7;
  const elapsedDays = Math.floor((isoDayToUtcMs(todayDay) - isoDayToUtcMs(missions.seasonStart)) / DAY_MS);
  const index = Math.floor(elapsedDays / lengthDays);
  const startDay = addDays(missions.seasonStart, index * lengthDays);
  const endDay = addDays(startDay, lengthDays);
  return { seasonId: startDay, startDay, endDay, index, lengthWeeks };
}

export function daysLeftInSeason(window, todayDay = todayIsoDay()) {
  if (!window || !isIsoDay(todayDay)) return 0;
  return Math.max(0, Math.round((isoDayToUtcMs(window.endDay) - isoDayToUtcMs(todayDay)) / DAY_MS));
}

// Monday of the week containing `day` — mirror of functions/gamificationGame
// weekKeyFor (questWeeks doc ids are `${weekKey}_${userDocId}`).
export function weekKeyForDay(day) {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay(); // 0=Sun..6=Sat
  return addDays(day, -((dow + 6) % 7));
}
